import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { SearchableSelect } from '@/components/SearchableSelect';
import { DocumentDetailSheet, DOC_CATEGORIES } from './DocumentDetailSheet';
import { formatBytes, errMessage, uploadDocument } from './utils';

// Section B: record-scoped documents list. Uploads real files via the
// multipart endpoint; the new document is locked to this record by default,
// but (when a property context exists) may instead be attached to a specific
// DOB Filing / DOT Permit on that property.
export function DocumentsSection({ recordType, recordId, contextPropertyId = null }) {
  const [docs, setDocs] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [linkRecords, setLinkRecords] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);

  const fetchDocs = useCallback(() => {
    if (!recordType || !recordId) { setDocs([]); return; }
    api.get('/documents', { params: { record_type: recordType, record_id: recordId } })
      .then(r => setDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDocs([]));
  }, [recordType, recordId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // Fetch the property-scoped filings/permits for the "Attach To" picker.
  useEffect(() => {
    const target = form.link_target;
    const PERMIT_URLS = { 'DOB Filing': '/dob-filings', 'DOT Permit': '/dot-permits', 'FDNY Permit': '/fdny-permits' };
    if (!contextPropertyId || !PERMIT_URLS[target]) { setLinkRecords([]); return; }
    setLinkLoading(true);
    api.get(PERMIT_URLS[target], { params: { property_id: contextPropertyId } })
      .then(r => setLinkRecords(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLinkRecords([]))
      .finally(() => setLinkLoading(false));
  }, [form.link_target, contextPropertyId]);

  const openAdd = () => { setForm({ name: '', category: 'Other', expiration_date: '', link_target: 'self', link_id: '' }); setFile(null); setProgress(0); setAddOpen(true); };

  const onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
    if (f && !form.name) setForm(prev => ({ ...prev, name: f.name }));
  };

  const linkOptions = (Array.isArray(linkRecords) ? linkRecords : []).map(r => {
    if (form.link_target === 'DOB Filing') return { value: r.id, label: `${r.job_number || 'Filing'}${r.filing_type ? ` · ${r.filing_type}` : ''}${r.description ? ` - ${r.description}` : ''}` };
    if (form.link_target === 'DOT Permit') return { value: r.id, label: `${r.permit_code || 'Permit'}${r.permit_number ? ` · ${r.permit_number}` : ''}${r.description ? ` - ${r.description}` : ''}` };
    return { value: r.id, label: `${r.permit_type || 'Permit'}${r.permit_number ? ` · ${r.permit_number}` : ''}` };
  });
  const isPermitTarget = form.link_target === 'DOB Filing' || form.link_target === 'DOT Permit' || form.link_target === 'FDNY Permit';

  const handleAdd = async () => {
    if (!file) return toast.error('Please choose a file to upload');
    const rt = isPermitTarget ? form.link_target : recordType;
    const rid = isPermitTarget ? form.link_id : recordId;
    if (isPermitTarget && !rid) return toast.error(`Select a specific ${form.link_target.toLowerCase()}`);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', (form.name || '').trim() || file.name);
    fd.append('category', form.category || 'Other');
    fd.append('record_type', rt);
    fd.append('record_id', rid);
    // Expiration only applies to non-permit links (permit-linked docs mirror the record live).
    if (!isPermitTarget && form.expiration_date) fd.append('expiration_date', form.expiration_date);
    setUploading(true); setProgress(0);
    try {
      await uploadDocument(fd, (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); });
      toast.success('Document uploaded');
      setAddOpen(false);
      fetchDocs();
    } catch (e) {
      toast.error(errMessage(e, 'Upload failed'));
    } finally { setUploading(false); }
  };

  const openDetail = (d) => { setSelected(d); setDetailOpen(true); };

  return (
    <div className="space-y-3" data-testid="documents-section">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{docs.length} document(s)</p>
        <Button size="sm" onClick={openAdd} className="bg-primary text-primary-foreground" data-testid="add-document-btn">
          <Upload className="h-3 w-3 mr-1" /> Add Document
        </Button>
      </div>

      <div className="border border-border rounded-md bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Name</TableHead>
              <TableHead className="text-xs text-muted-foreground">File</TableHead>
              <TableHead className="text-xs text-muted-foreground">Category</TableHead>
              <TableHead className="text-xs text-muted-foreground">Upload Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.map(d => (
              <TableRow key={d.id} className="border-border hover:bg-secondary/30 cursor-pointer" onClick={() => openDetail(d)} data-testid={`doc-row-${d.id}`}>
                <TableCell className="text-sm font-medium" style={{ color: '#010A21' }}>{d.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground" data-testid={`doc-file-${d.id}`}>
                  {d.original_filename ? (
                    <span>{d.original_filename}{d.file_size ? <span className="text-xs"> · {formatBytes(d.file_size)}</span> : null}</span>
                  ) : '\u00A0'}
                </TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{d.category}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground">{d.created_at ? new Date(d.created_at).toLocaleDateString() : '\u00A0'}</TableCell>
              </TableRow>
            ))}
            {docs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-6">No documents linked to this record</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Add document form */}
      <Sheet open={addOpen} onOpenChange={(o) => { if (!uploading) setAddOpen(o); }}>
        <SheetContent className="sm:max-w-md bg-card border-border overflow-y-auto" data-testid="doc-add-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading">Add Document</SheetTitle>
            <SheetDescription>Linked to this {(recordType || 'record').toLowerCase()}</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div>
              <Label className="text-xs text-muted-foreground">File *</Label>
              <Input type="file" onChange={onFileChange} className="bg-background border-border cursor-pointer file:text-sm file:mr-3 file:border-0 file:bg-secondary file:px-2 file:py-1 file:rounded" data-testid="doc-add-file" />
              {file && <p className="text-xs text-muted-foreground mt-1" data-testid="doc-add-file-info">{file.name} · {formatBytes(file.size)}</p>}
            </div>
            <div><Label className="text-xs text-muted-foreground">Document Name *</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border" data-testid="doc-add-name" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={form.category || 'Other'} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-background border-border" data-testid="doc-add-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">{DOC_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {contextPropertyId && (
              <div>
                <Label className="text-xs text-muted-foreground">Attach To</Label>
                <Select value={form.link_target || 'self'} onValueChange={v => setForm({ ...form, link_target: v, link_id: '' })}>
                  <SelectTrigger className="bg-background border-border" data-testid="doc-add-link-target"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="self">This {(recordType || 'record').toLowerCase()}</SelectItem>
                    <SelectItem value="DOB Filing">DOB Filing</SelectItem>
                    <SelectItem value="DOT Permit">DOT Permit</SelectItem>
                    <SelectItem value="FDNY Permit">FDNY Permit</SelectItem>
                  </SelectContent>
                </Select>
                {isPermitTarget && (
                  <div className="mt-2">
                    <SearchableSelect
                      options={linkOptions}
                      value={form.link_id || ''}
                      onChange={v => setForm({ ...form, link_id: v || '' })}
                      placeholder={linkLoading ? 'Loading...' : `Select ${form.link_target.toLowerCase()}`}
                      searchPlaceholder={`Search ${form.link_target.toLowerCase()}s...`}
                      emptyText={linkLoading ? 'Loading...' : `No ${form.link_target.toLowerCase()}s on this property.`}
                      testid="doc-add-link-record"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Scoped to this property&rsquo;s {form.link_target === 'DOB Filing' ? 'filings' : 'permits'}.</p>
                  </div>
                )}
              </div>
            )}
            {isPermitTarget ? (
              <div className="rounded-md border border-border bg-secondary/20 px-3 py-2">
                <p className="text-xs text-muted-foreground" data-testid="doc-add-expiration-note">Expiration is read live from the linked {form.link_target} and cannot be set here.</p>
              </div>
            ) : (
              <div><Label className="text-xs text-muted-foreground">Expiration Date <span className="text-muted-foreground/60">(optional)</span></Label><Input type="date" value={form.expiration_date || ''} onChange={e => setForm({ ...form, expiration_date: e.target.value })} className="bg-background border-border" data-testid="doc-add-expiration" /></div>
            )}
            {uploading && (
              <div data-testid="doc-add-progress">
                <div className="w-full h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} /></div>
                <p className="text-xs text-muted-foreground mt-1">Uploading... {progress}%</p>
              </div>
            )}
            <Button onClick={handleAdd} disabled={uploading} className="w-full bg-primary text-primary-foreground mt-2" data-testid="doc-add-submit">
              {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading...</> : 'Upload Document'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <DocumentDetailSheet open={detailOpen} onOpenChange={setDetailOpen} doc={selected} onChanged={() => fetchDocs()} onDeleted={() => fetchDocs()} contextPropertyId={contextPropertyId} />
    </div>
  );
}

export default DocumentsSection;
